import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowForward as ArrowForwardIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface AdminLoginProps {
  onBackToClientLogin?: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onBackToClientLogin }) => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(loginId, password);
    if (!result.success) { setError(result.error || 'Login failed'); }
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f6f6f8', px: 2 }}>
      {/* Logo */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: '#4b2bee', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <ShieldIcon sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '1.75rem', color: '#0f172a', mb: 0.5 }}>
          Admin Portal
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.875rem', letterSpacing: 1.5, textTransform: 'uppercase' }}>
          Super Administrator Access
        </Typography>
      </Box>

      {/* Card */}
      <Box sx={{ width: '100%', maxWidth: 400, bgcolor: '#ffffff', borderRadius: 3, p: 4, boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.04)' }}>
        {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, border: 'none' }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#475569', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.8 }}>
            Admin ID
          </Typography>
          <TextField
            fullWidth value={loginId} onChange={(e) => setLoginId(e.target.value)}
            placeholder="Enter admin login ID" required autoFocus
            sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff', '& fieldset': { borderColor: 'rgba(226,232,240,0.5)' }, '&:hover fieldset': { borderColor: '#e2e8f0' }, '&.Mui-focused fieldset': { borderColor: '#4b2bee', boxShadow: '0 0 0 3px rgba(75,43,238,0.1)' } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment> }}
          />

          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#475569', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.8 }}>
            Password
          </Typography>
          <TextField
            fullWidth type={showPassword ? 'text' : 'password'} value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
            sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff', '& fieldset': { borderColor: 'rgba(226,232,240,0.5)' }, '&:hover fieldset': { borderColor: '#e2e8f0' }, '&.Mui-focused fieldset': { borderColor: '#4b2bee', boxShadow: '0 0 0 3px rgba(75,43,238,0.1)' } } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment>,
              endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" sx={{ color: '#94a3b8' }}>{showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></InputAdornment>,
            }}
          />

          <Button type="submit" fullWidth variant="contained" disabled={loading} endIcon={!loading && <ArrowForwardIcon />}
            sx={{ bgcolor: '#4b2bee', borderRadius: 2, py: 1.5, textTransform: 'none', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.9375rem', boxShadow: '0 4px 12px rgba(75,43,238,0.25)', '&:hover': { bgcolor: '#3d1fd4' }, '&.Mui-disabled': { bgcolor: '#e2e8f0' } }}
          >
            {loading ? 'Signing In...' : 'Sign In as Admin'}
          </Button>
        </Box>

        <Typography sx={{ textAlign: 'center', mt: 3, fontFamily: 'Inter, sans-serif', color: '#475569', fontSize: '0.875rem' }}>
          <Link component="button" onClick={(e: React.MouseEvent) => { e.preventDefault(); onBackToClientLogin?.(); }} type="button"
            sx={{ color: '#4b2bee', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
          >
            ← Back to Client Login
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminLogin;
